import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearch, useSearchByYear } from '../../api/search';
import { Search as SearchIcon, Calendar, ChevronRight } from 'lucide-react';
import type { SearchResult } from '../../types/api';

export function SearchInterface() {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [searchYear, setSearchYear] = useState('');
    const [searchMode, setSearchMode] = useState<'name' | 'year'>('name');

    const nameSearch = useSearch(searchQuery, 20, 0);
    const yearSearch = useSearchByYear(
        searchYear ? parseInt(searchYear) : 0
    );

    const results = searchMode === 'name' ? nameSearch.data : yearSearch.data;
    const isLoading = searchMode === 'name' ? nameSearch.isLoading : yearSearch.isLoading;

    const handleResultClick = (result: SearchResult) => {
        navigate(`/person/${result.id}`);
    };

    return (
        <div className="card">
            <h2 className="text-2xl font-bold mb-4">Tìm kiếm</h2>

            {/* Search Mode Tabs */}
            <div className="flex gap-2 mb-4">
                <button
                    onClick={() => setSearchMode('name')}
                    className={`px-4 py-2 rounded-md font-medium transition-colors ${searchMode === 'name'
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                >
                    <SearchIcon className="w-4 h-4 inline mr-2" />
                    Tìm theo tên
                </button>
                <button
                    onClick={() => setSearchMode('year')}
                    className={`px-4 py-2 rounded-md font-medium transition-colors ${searchMode === 'year'
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                >
                    <Calendar className="w-4 h-4 inline mr-2" />
                    Tìm theo năm
                </button>
            </div>

            {/* Search Inputs */}
            {searchMode === 'name' ? (
                <div className="mb-4">
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Nhập tên cần tìm..."
                        className="input"
                    />
                </div>
            ) : (
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Năm sinh
                    </label>
                    <input
                        type="number"
                        value={searchYear}
                        onChange={(e) => setSearchYear(e.target.value)}
                        placeholder="VD: 1950"
                        className="input"
                    />
                </div>
            )}

            {/* Results */}
            <div className="space-y-2 max-h-80 overflow-y-auto">
                {isLoading && (
                    <div className="text-center py-8 text-gray-500">
                        <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                        Đang tìm kiếm...
                    </div>
                )}

                {!isLoading && results && results.length === 0 && (searchQuery || searchYear) && (
                    <div className="text-center py-8 text-gray-500">
                        Không tìm thấy kết quả
                    </div>
                )}

                {!isLoading && results && results.length > 0 && (
                    <>
                        <p className="text-sm text-gray-600 mb-2">
                            Tìm thấy {results.length} kết quả
                        </p>
                        {results.map((result) => (
                            <div
                                key={result.id}
                                onClick={() => handleResultClick(result)}
                                className="p-3 border border-gray-200 rounded-lg hover:bg-primary-50 hover:border-primary-200 cursor-pointer transition-colors group"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 text-sm font-semibold flex-shrink-0">
                                            {result.fullName.charAt(0)}
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-900 group-hover:text-primary-700">
                                                {result.fullName}
                                            </h3>
                                            <div className="flex gap-3 text-xs text-gray-500">
                                                <span>
                                                    {result.gender === 'MALE' ? '👨 Nam' : result.gender === 'FEMALE' ? '👩 Nữ' : '🧑 Khác'}
                                                </span>
                                                {result.birthDate && (
                                                    <span>
                                                        Sinh: {new Date(result.birthDate).getFullYear()}
                                                    </span>
                                                )}
                                                {result.parents && result.parents.length > 0 && (
                                                    <span className="text-gray-400">
                                                        Con của: {result.parents.map(p => p.fullName).join(', ')}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-primary-400" />
                                </div>
                            </div>
                        ))}
                    </>
                )}
            </div>
        </div>
    );
}
